import { useMutation, useQuery } from '@apollo/client';
import { Tooltip } from '@mui/material';
import { gql } from '__generated__/gql';
import { ParticipantStatusEnumType } from '__generated__/graphql';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import FocusLayout from 'layouts/FocusLayout';

import ErrorMessage from 'components/ErrorMessage';

import ConfirmButton from 'primitives/ConfirmButton';
import LoadingIndicator from 'primitives/LoadingIndicator';
import Toast from 'primitives/Toast';
import useToast from 'primitives/Toast/useToast';

import DealInformation from '../DealInvite/DealInformation';

const REBALANCE_MUTATION = gql(`
  mutation RebalanceFundDealParticipants($fundClosingId: ID!, $dealClosingId: ID!, $status: ParticipantStatusEnumType!, $fundId: ID!, $dealId: ID!) {
    rebalanceFundDealParticipants(fundClosingId: $fundClosingId, dealClosingId: $dealClosingId, status: $status, fundId: $fundId, dealId: $dealId) {
      id
      amountInvested
      ownershipPercentage
    }
  }
`);

const FUND_QUERY = gql(`
    query fetchFund($fundIdFor: ID!) {
      fund(id: $fundIdFor) {
        id
        name
        image
        fundSize
        syndicate {
          id
        }
        fundManagers {
          user {
            name
          }
        }
        userFundClosing {
          id
          investmentEntity {
            id
            name
          }
        }
      }
    }
  `);

const DEAL_CLOSING_QUERY = gql(`
  query GetDealClosingForFund($dealId: ID!) {
    deal(id: $dealId) {
        id
        companyInformation {
          id
          company {
            id
            name
            image
          }
          industries
          memo
        }
        syndicate {
          id
          name
          type
        }
        dealDisclosures {
          id
          risksNote
          otherDisclosuresNote
          hasLeadInvestedInCompanyBefore
          doesLeadHaveAdvisoryShares    
          isLeadEmployeeAtCompany       
          doesLeadHaveRelativesInCompany
          hasSameTermsForCoInvestors    
          isLeadInvestingOnSameTerms    
          isLeadInvestingFromFund       
        }
        dealTerms {
          id
          transactionType
          fundingRoundSize
          fundingRoundName
          financialInstrument
          valuationOrCap
          discount
          pastFinancingNote
          prorataRights
          minimumInvestment
          availableCarryPercentage
        }
        type
        allocation
        closingDate
        gpCommitment
        externalInvestors {
          id
          name
          description
          amount
          isLead
        }
    }
  }
`);

export default function FundInviteLPConsent() {
  const { fundId, dealClosingId, dealId } = useParams<{
    fundId: string;
    dealClosingId: string;
    dealId: string;
  }>();

  const navigate = useNavigate();
  const { showToast, handleToastOpen, handleToastClose } = useToast();
  const {
    showToast: showToastError,
    handleToastOpen: handleToastOpenError,
    handleToastClose: handleToastCloseError,
  } = useToast();

  const { loading, error, data, refetch } = useQuery(FUND_QUERY, {
    variables: {
      fundIdFor: fundId || '',
    },
  });

  const {
    loading: dealClosingLoading,
    error: dealClosingError,
    data: dealClosingData,
    refetch: dealClosingRefetch,
  } = useQuery(DEAL_CLOSING_QUERY, {
    variables: {
      dealId: dealId || '',
    },
  });

  const [rebalanceParticipants, { loading: rebalanceLoading, error: rebalanceError }] =
    useMutation(REBALANCE_MUTATION);

  const handleRebalance = async (status: ParticipantStatusEnumType) => {
    try {
      const { data: rebalanceData } = await rebalanceParticipants({
        variables: {
          fundClosingId: data?.fund?.userFundClosing?.id || '',
          status: status,
          fundId: fundId || '',
          dealClosingId: dealClosingId || '',
          dealId: dealId || '',
        },
      });

      if (rebalanceData) {
        handleToastOpen();
        navigate('/invest');
      }
    } catch (error: any) {
      handleToastOpenError();
    }
  };

  if (loading || dealClosingLoading) return <LoadingIndicator />;

  if (error || !data || dealClosingError || !dealClosingData)
    return <ErrorMessage error={error} refetch={refetch} />;

  // Redirect to portfolio if the user is not a part of this fund
  if (!data?.fund?.userFundClosing?.id) {
    return <Navigate to="/invest" />;
  }

  return (
    <FocusLayout>
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-2xl font-bold">Consent to invest in {data.fund.name}</h1>
        </div>
        <div>
          <DealInformation
            deal={dealClosingData?.deal}
            fundManager={data?.fund.fundManagers[0]?.user?.name}
          />
        </div>
      </div>

      <div className="col-span-2 mb-32">
        <label htmlFor="investmentEntity" className="block text-sm font-medium text-gray-700">
          <Tooltip title="You cannot change your investment entity" placement="top">
            <span>Your Investment Entity</span>
          </Tooltip>
        </label>
        <select
          id="investmentEntity"
          name="investmentEntity"
          className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm cursor-not-allowed"
          defaultValue={data.fund.userFundClosing.investmentEntity.id}
          disabled
        >
          <option value={data.fund.userFundClosing.investmentEntity.id}>
            {data.fund.userFundClosing.investmentEntity.name}
          </option>
        </select>
      </div>
      <div className="fixed bottom-4 left-0 right-0">
        <div className="mx-auto max-w-3xl bg-white shadow-2xl p-4 grid grid-cols-6 gap-x-3 border-2 border-indigo-100 rounded-lg">
          <div className="col-span-2">
            <h2 className="text-2xl font-medium">Consent</h2>
            <p className="text-sm text-gray-400 my-1">Please select an option</p>
          </div>

          <div className="col-span-2 h-full">
            <ConfirmButton
              className="w-full h-full"
              onConfirm={() => handleRebalance(ParticipantStatusEnumType.OptedOut)}
              loading={rebalanceLoading}
              title="Opt Out"
              description="Are you sure you want to opt out of this deal?"
              variant="secondary"
            >
              Decline
            </ConfirmButton>
          </div>
          <div className="col-span-2 h-full">
            <ConfirmButton
              className="w-full h-full"
              onConfirm={() => handleRebalance(ParticipantStatusEnumType.OptedIn)}
              loading={rebalanceLoading}
              title="Opt In"
              description="Are you sure you want to opt in to this deal?"
              variant="primary"
            >
              Accept
            </ConfirmButton>
          </div>
        </div>
        <Toast
          type="success"
          title="Commitment Submitted"
          message={
            'We will notify the fund manager of your interest in this Fund. Wiring instructions will be sent to you once the fund manager has confirmed your investment.'
          }
          show={showToast}
          handleClose={handleToastClose}
        />
        <Toast
          type="error"
          title="Error"
          show={showToastError}
          handleClose={handleToastCloseError}
          message={
            rebalanceError?.message || 'We were unable to submit your commitment. Please try again.'
          }
        />
      </div>
    </FocusLayout>
  );
}
